import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Panel, Persona, PersonaSize, PrimaryButton } from '@fluentui/react'
import { Store } from '../reducers'
import { useHistory } from 'react-router-dom'
import { isAppContext } from '../helpers/appHelper'
import { AppContext } from '../config/appConfig'
import useUser from '../hooks/useUser'
import useUsers from '../hooks/useUsers'
import { ModuleType } from '../context/GlobalContext'
import useModules from '../hooks/module/useModules'
import useRoles, { Role, rolesTasks, rolesTimes } from '../hooks/useRoles'
import { Identity } from '../context/IdentityContext'
import useSharePoint from '../hooks/useSharePoint'
import { Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { useSettings } from '../hooks/useSettings'
import { useTranslation } from 'react-i18next'
import {
    AlbumFilled,
    AlbumRegular,
    ArrowTrendingLinesFilled,
    ArrowTrendingLinesRegular,
    bundleIcon,
    ClipboardClockFilled,
    ClipboardClockRegular,
    ClipboardTaskFilled,
    ClipboardTaskRegular,
    DocumentCatchUpFilled,
    DocumentCatchUpRegular,
    FlowchartFilled,
    FlowchartRegular,
    GiftCardFilled,
    GiftCardRegular,
    HomeFilled,
    HomeRegular,
    MailFilled,
    MailRegular,
    PaymentFilled,
    PaymentRegular,
    PeopleFilled,
    PeopleRegular,
    TextColumnOneWideFilled,
    TextColumnOneWideRegular,
    TimerFilled,
    TimerRegular,
    VehicleCarProfileLtrClockFilled,
    VehicleCarProfileLtrClockRegular,
} from '@fluentui/react-icons'
import { SharePointIcon } from './Icon/SharePointIcon'
import { Timers } from './Timer/Timers'
import useViewport, { Viewport } from '../hooks/useViewport'
import OverflowTabListPanel, { HammockButtonProps, HammockTabData, HammockTabListProps, HammockTabProps } from './Overflow/OverflowTabListPanel'
import { SelectTabData, SelectTabEvent } from '@fluentui/react-tabs'

type TabItem = HammockTabData & { route?: string; filter?: () => boolean }

export enum MenuItemKey {
    Dashboard = 'dashboard',
    Files = 'files',
    Parties = 'parties',
    Emails = 'emails',
    Documents = 'documents',
    Tasks = 'tasks',
    Workflow = 'workflows',
    Times = 'times',
    InvoiceApproval = 'invoice-approval',
    Invoice = 'invoice',
    Reports = 'reports',
    SharePoint = 'sharepoint',
}

export const AlbumIcon = bundleIcon(AlbumFilled, AlbumRegular)
export const ArrowTrendingLinesIcon = bundleIcon(ArrowTrendingLinesFilled, ArrowTrendingLinesRegular)
export const ClipboardTaskIcon = bundleIcon(ClipboardTaskFilled, ClipboardTaskRegular)
export const DocumentBriefcaseIcon = bundleIcon(DocumentCatchUpFilled, DocumentCatchUpRegular)
export const DisbursementIcon = bundleIcon(PaymentFilled, PaymentRegular)
export const FlowchartIcon = bundleIcon(FlowchartFilled, FlowchartRegular)
export const FixedPriceIcon = bundleIcon(GiftCardFilled, GiftCardRegular)
export const HomeIcon = bundleIcon(HomeFilled, HomeRegular)
export const MailIcon = bundleIcon(MailFilled, MailRegular)
export const PeopleIcon = bundleIcon(PeopleFilled, PeopleRegular)
export const TimerIcon = bundleIcon(TimerFilled, TimerRegular)
export const ClipboardClockIcon = bundleIcon(ClipboardClockFilled, ClipboardClockRegular)
export const TextColumnOneWideIcon = bundleIcon(TextColumnOneWideFilled, TextColumnOneWideRegular)
export const TravelIcon = bundleIcon(VehicleCarProfileLtrClockFilled, VehicleCarProfileLtrClockRegular)

const useStyles = makeStyles({
    container: {
        backgroundColor: tokens.colorBrandBackground2,
        display: 'flex',
        justifyContent: 'space-between',
        height: '100%',
    },
    leftContainer: {
        flexGrow: 1,
    },
    farContainer: {
        width: '80px',
    },
    farContainerSmall: {
        gap: tokens.spacingHorizontalS,
        width: '56px',
    },
    button: {
        padding: tokens.spacingVerticalS,
    },
    buttonXS: {
        padding: tokens.spacingVerticalXS,
    },
})

function MainMenuFarItems(props: { buttonProps: HammockButtonProps; containerClassName: string }): ReactElement {
    const modules = useModules()
    const roles = useRoles()
    const { t: translate } = useTranslation()
    const { currentUser } = useUser()
    const styles = useStyles()

    const [isHamburgerOpen, toggleHamburger] = useState<boolean>(false)

    return (
        <div className={mergeClasses(styles.container, props.containerClassName)}>
            {modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes) && <Timers buttonProps={props.buttonProps} />}
            <Button {...props.buttonProps} value={'menu'} icon={<TextColumnOneWideIcon />} onClick={() => toggleHamburger(!isHamburgerOpen)} />
            <Panel isOpen={isHamburgerOpen} onDismiss={() => toggleHamburger(false)}>
                <Persona text={currentUser?.fullName} key="current-persona" size={PersonaSize.size24} />
                <PrimaryButton text={translate('menu.logout')} iconProps={{ iconName: 'Leave' }} onClick={() => Identity.current.logout()} />
            </Panel>
        </div>
    )
}

export default function MainMenu(): ReactElement {
    const history = useHistory()
    const modules = useModules()
    const roles = useRoles()
    const settings = useSettings()
    const { sharePoint } = useSharePoint()
    const users = useUsers()
    const styles = useStyles()
    const { viewport, isViewport } = useViewport()

    const breadcrumbs = useSelector<Store, string[]>(state => state.app.breadcrumbs)
    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])
    const viewportOutlookWeb = useMemo<boolean>(() => isViewport('<', Viewport.OutlookWeb), [viewport])

    const buttonProps = useMemo<HammockButtonProps>(
        () => ({
            appearance: 'subtle',
            className: viewportOutlookWeb ? styles.buttonXS : styles.button,
            size: smallMenu ? 'small' : 'large',
        }),
        [smallMenu, viewportOutlookWeb],
    )
    const tabListProps = useMemo<HammockTabListProps>(
        (): HammockTabListProps => ({
            size: smallMenu ? 'small' : 'large',
            appearance: 'subtle',
            selectTabOnFocus: true,
        }),
        [smallMenu],
    )
    const tabProps = useMemo<HammockTabProps>(
        (): HammockTabProps => ({
            className: viewportOutlookWeb ? styles.buttonXS : styles.button,
        }),
        [viewportOutlookWeb],
    )

    const onTabSelect = (e: SelectTabEvent, data: SelectTabData) => {
        const tabItem: TabItem = tabs.find(tab => tab.value === data.value)
        if (tabItem?.route) {
            history.push(tabItem?.route)
        } else if (tabItem?.onClick) {
            tabItem.onClick(undefined)
        }
    }

    const tabs = useMemo<TabItem[]>(
        (): TabItem[] =>
            [
                {
                    value: MenuItemKey.Dashboard,
                    icon: <HomeIcon />,
                    titleKey: 'menu.dashboard',
                    route: '/',
                    filter: () => settings?.dashboard?.enabled !== false,
                },
                {
                    value: MenuItemKey.Files,
                    icon: <AlbumIcon />,
                    titleKey: 'menu.file',
                    route: '/file',
                },
                {
                    value: MenuItemKey.Parties,
                    icon: <PeopleIcon />,
                    titleKey: 'menu.party',
                    route: '/party',
                },
                {
                    value: MenuItemKey.Emails,
                    icon: <MailIcon />,
                    titleKey: 'menu.email',
                    route: '/email',
                    filter: () => isAppContext(AppContext.Read) || isAppContext(AppContext.None),
                },
                {
                    value: MenuItemKey.Tasks,
                    icon: <ClipboardTaskIcon />,
                    titleKey: 'menu.task',
                    route: '/task',
                    filter: () => modules.has(ModuleType.Tasks) && roles.any(rolesTasks),
                },
                {
                    value: MenuItemKey.Workflow,
                    icon: <FlowchartIcon />,
                    titleKey: 'menu.workflow',
                    route: '/workflow',
                    filter: () => modules.has(ModuleType.Workflow) && roles.any(rolesTasks),
                },
                {
                    value: MenuItemKey.Times,
                    icon: <TimerIcon />,
                    titleKey: 'menu.time',
                    route: '/time',
                    filter: () => modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes),
                },
                {
                    value: MenuItemKey.InvoiceApproval,
                    icon: <ClipboardClockIcon />,
                    titleKey: 'menu.invoice.approval',
                    route: '/invoice-approval',
                    filter: () => modules.has(ModuleType.InvoiceApproval) && roles.any(rolesTimes),
                },
                {
                    value: MenuItemKey.Invoice,
                    icon: <ArrowTrendingLinesIcon />,
                    titleKey: 'menu.invoice',
                    route: '/invoice',
                    filter: () => modules.has(ModuleType.Invoices) && roles.any([Role.Invoice]),
                },
                {
                    value: MenuItemKey.Reports,
                    icon: <DocumentBriefcaseIcon />,
                    titleKey: 'menu.reports',
                    route: '/reports',
                    filter: () => modules.has(ModuleType.Reports) && roles.any([Role.Report]),
                },
                {
                    value: MenuItemKey.SharePoint,
                    icon: <SharePointIcon />,
                    titleKey: 'menu.sharepoint',
                    onClick: () => window.open(sharePoint?.list?.webUrl, '_blank'),
                },
            ].filter((item: TabItem) => (item.filter ? item.filter() : true)),
        [breadcrumbs, roles.loaded, users.loaded, modules.loaded, settings],
    )

    if (!(roles.loaded && users.loaded && modules.loaded && settings)) {
        return <></>
    }

    return (
        <div className={styles.container}>
            <OverflowTabListPanel
                tabListProps={tabListProps}
                tabProps={tabProps}
                buttonProps={buttonProps}
                containerClassName={mergeClasses(styles.container, styles.leftContainer)}
                tabs={tabs}
                onTabSelect={onTabSelect}
                selectedValue={breadcrumbs?.[0]}
                overflowButtonLabelKey={'base.item.label'}
            />
            <MainMenuFarItems buttonProps={buttonProps} containerClassName={smallMenu ? styles.farContainerSmall : styles.farContainer} />
        </div>
    )
}
